import { 
  Wrench, Activity, Package, Truck, TrendingUp, Mail, MessageSquare, Users2, 
  Shield, BarChart3, Database, Globe, Lock, CheckCircle, Clock, Settings, 
  Smartphone, Leaf, Zap, Brain, Eye, Target, Star, CreditCard, Building2
} from 'lucide-react';
import { ProductConfigs } from './types';

export const missingIntegrationBeautySolutionsConfigs: ProductConfigs = {
  // Integration & System Solutions
  'appsecconnect-beauty': {
    keyBenefits: [
      {
        icon: Wrench,
        title: 'Beauty System Integration Platform',
        description: 'Comprehensive Integration Platform-as-a-Service connecting beauty business systems (ERP, e-commerce, CRM) with automated data synchronization.',
        metrics: '95% system integration success rate',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Real-Time Data Synchronization',
        description: 'Seamless real-time data flow between beauty business systems ensuring consistent information across all platforms.',
        metrics: '99.9% data synchronization accuracy',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Settings,
        title: 'Custom Workflow Automation',
        description: 'Flexible workflow automation for beauty business processes, inventory updates, and customer data management.',
        metrics: '80% operational efficiency improvement',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Database,
        title: 'Unified Beauty Data Management',
        description: 'Centralized data management system unifying product catalogs, customer information, and business analytics.',
        metrics: '90% data consistency improvement',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Platform Connectivity',
        description: 'Connect unlimited beauty systems and platforms with pre-built connectors and custom API integrations.',
        metrics: 'Support for 200+ beauty platforms',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Beauty Business Intelligence',
        description: 'Advanced analytics combining data from all integrated systems for comprehensive beauty business insights.',
        metrics: 'Unified analytics from all systems',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Integration Intelligence',
        description: 'Machine learning algorithms optimizing data flows, detecting integration issues, and recommending improvements.',
        metrics: '92% automated issue resolution',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Integration Monitoring',
        description: 'Advanced monitoring system tracking integration performance, data quality, and system health in real-time.',
        metrics: 'Real-time integration visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Integration Management',
        description: 'Complete mobile platform for managing integrations, monitoring data flows, and resolving issues on-the-go.',
        metrics: '4.8/5 mobile management rating',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Custom Beauty Connector Development',
        description: 'Flexible connector development platform enabling custom integrations for specialized beauty systems.',
        metrics: 'Unlimited custom connectors',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Package,
        title: 'Beauty Inventory Synchronization',
        description: 'Advanced inventory management ensuring product data, stock levels, and pricing consistency across all channels.',
        metrics: '98% inventory accuracy maintenance',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: CreditCard,
        title: 'Payment System Integration',
        description: 'Comprehensive payment integration connecting beauty POS systems, e-commerce platforms, and financial reporting.',
        metrics: '99.9% payment data accuracy',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'clearforme-ingredients': {
    keyBenefits: [
      {
        icon: Leaf,
        title: 'Clean Beauty Ingredient Intelligence',
        description: 'Comprehensive ingredient analysis platform providing transparency and education about beauty product components.',
        metrics: '99.9% ingredient database accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Shield,
        title: 'Safety & Allergen Analysis',
        description: 'Advanced safety analysis identifying potential allergens, irritants, and harmful ingredients for consumer protection.',
        metrics: '100% safety compliance verification',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Eye,
        title: 'Ingredient Transparency Platform',
        description: 'Complete transparency platform enabling consumers to understand every ingredient in their beauty products.',
        metrics: '95% consumer trust improvement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Activity,
        title: 'Regulatory Compliance Tracking',
        description: 'Automated regulatory compliance monitoring for global beauty standards and ingredient regulations.',
        metrics: 'Global regulatory compliance',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Target,
        title: 'Personalized Ingredient Recommendations',
        description: 'AI-powered ingredient recommendations based on skin type, concerns, and personal preferences.',
        metrics: '90% personalization accuracy',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Clean Beauty Market Intelligence',
        description: 'Advanced market intelligence tracking clean beauty trends, ingredient innovations, and consumer preferences.',
        metrics: 'Real-time market insights',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Ingredient Analysis Engine',
        description: 'Machine learning algorithms analyzing ingredient interactions, efficacy, and safety for comprehensive evaluation.',
        metrics: '96% analytical accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Ingredient Database',
        description: 'Extensive database of beauty ingredients with detailed information, research studies, and safety profiles.',
        metrics: 'Database of 50K+ ingredients',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Ingredient Scanner',
        description: 'Advanced mobile app enabling instant ingredient analysis through barcode scanning and camera recognition.',
        metrics: '4.9/5 mobile app rating',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Settings,
        title: 'Custom Safety Profiles',
        description: 'Flexible safety profile customisation for different skin types, allergies, and personal preferences.',
        metrics: 'Unlimited safety customisation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Global Regulatory Intelligence',
        description: 'Comprehensive regulatory intelligence covering beauty standards across 100+ countries and regions.',
        metrics: 'Global regulatory coverage',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Star,
        title: 'Professional Formulation Support',
        description: 'Advanced formulation support tools helping beauty professionals create safe, effective products.',
        metrics: 'Professional formulation excellence',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};